import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Clock, CheckCircle2, XCircle, Play, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_ORDER = ["youtube", "facebook", "instagram", "tiktok"];
const PLATFORM_LABEL = { youtube: "YT", facebook: "FB", instagram: "IG", tiktok: "TT" };

function StatusIcon({ status }) {
  if (status === "pending") return <Clock className="h-3 w-3 text-zinc-500" />;
  if (status === "running") return <Loader2 className="h-3 w-3 animate-spin text-amber-300" />;
  if (status === "success") return <CheckCircle2 className="h-3 w-3 text-emerald-400" />;
  if (status === "skipped") return <span className="text-[10px] text-zinc-500">↷</span>;
  if (status === "failed") return <XCircle className="h-3 w-3 text-red-400" />;
  return null;
}

function ClipCard({ slot, rows }) {
  if (!rows.length) return null;
  const first = rows[0];
  const byPlatform = Object.fromEntries(rows.map(r => [r.platform, r]));
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`plan-card-${slot}`}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300">{slot} clip</p>
        <p className="text-[10px] text-zinc-500">{first.clip_meta?.project_title || ""}</p>
      </div>
      <h3 className="mb-3 line-clamp-2 text-sm font-medium text-zinc-100" title={first.clip_title}>{first.clip_title}</h3>
      <div className="space-y-1.5">
        {PLATFORM_ORDER.map(p => {
          const r = byPlatform[p];
          if (!r) return null;
          const time = r.scheduled_at_local_str?.split(" ")[1] || "?";
          return (
            <div key={p} className="flex items-center justify-between rounded bg-zinc-950 px-2 py-1.5 text-xs ring-1 ring-zinc-800">
              <div className="flex items-center gap-2">
                <span className="inline-flex w-7 justify-center text-[10px] text-zinc-400">{PLATFORM_LABEL[p]}</span>
                <span className="text-zinc-500">{time}</span>
              </div>
              <div className="flex items-center gap-1.5" title={r.error_message || r.status}>
                <StatusIcon status={r.status} />
                {r.status === "success" && r.platform_url?.startsWith("http") && (
                  <a href={r.platform_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline text-[10px]">view</a>
                )}
                {r.status === "failed" && (
                  <span className="max-w-[120px] truncate text-[10px] text-red-300">{r.error_message}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TodaysPlan() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/schedule/today`);
      setData(r.data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s to catch live status
    return () => clearInterval(t);
  }, [load]);

  const reroll = async () => {
    if (!window.confirm("Re-roll today's clips? This deletes the current pending plan and picks 2 new random unposted clips.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/schedule/rebuild`, null, { timeout: 120000 });
      await load();
    } catch (e) {
      window.alert("Rebuild failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading plan…</div>;

  const morning = (data.rows || []).filter(r => r.slot === "morning");
  const evening = (data.rows || []).filter(r => r.slot === "evening");

  return (
    <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-5" data-testid="todays-plan">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300">Today's plan</p>
          <h2 className="text-lg font-medium text-zinc-100">{data.day_key} · {data.timezone}</h2>
          <p className="mt-1 text-xs text-zinc-400">
            {data.rows?.length === 0
              ? "No plan built yet. Use 'Re-roll' to build one."
              : `2 clips × 4 platforms. The post runner fires every minute.`}
          </p>
        </div>
        <button
          onClick={reroll}
          disabled={busy}
          data-testid="reroll-btn"
          className="inline-flex items-center gap-1.5 rounded bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 ring-1 ring-zinc-700 hover:bg-zinc-800 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Re-roll
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ClipCard slot="morning" rows={morning} />
        <ClipCard slot="evening" rows={evening} />
      </div>
    </div>
  );
}
